import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search } from 'lucide-react';

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({ name: '', description: '', head: '' });

  useEffect(() => { fetchDepartments(); }, []);

  const fetchDepartments = async () => {
    try {
      const res = await api.get('/purchase/departments');
      setDepartments(res.data);
    } catch (err) { console.error('Failed to fetch departments:', err); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditingDept(null); setFormData({ name: '', description: '', head: '' }); setShowModal(true); };
  const openEdit = (d) => { setEditingDept(d); setFormData({ name: d.name, description: d.description || '', head: d.head || '' }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDept) {
        await api.put(`/purchase/departments/${editingDept.id}`, formData);
      } else {
        await api.post('/purchase/departments', formData);
      }
      setShowModal(false);
      fetchDepartments();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to save department'); }
  };

  const deleteDept = async (id, name) => {
    if (!window.confirm(`Delete department "${name}"?`)) return;
    try { await api.delete(`/purchase/departments/${id}`); fetchDepartments(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  const filtered = departments.filter(d => {
    const matchSearch = d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.department_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.description && d.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchStatus = statusFilter === 'all' || d.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div data-testid="departments-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Department Master</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="add-dept-btn"><Plus size={16} /> Add Department</button>
      </div>

      <div className="card mb-4">
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
              <input type="text" className="form-input" placeholder="Search by name, code, or description..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading departments...</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr><th>Code</th><th>Name</th><th>Description</th><th>Head</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(d => (
                  <tr key={d.id} data-testid={`dept-row-${d.department_code}`}>
                    <td className="font-mono">{d.department_code}</td>
                    <td>{d.name}</td>
                    <td className="text-sm">{d.description || '-'}</td>
                    <td className="text-sm">{d.head || '-'}</td>
                    <td><span className={`badge ${d.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{d.status}</span></td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(d)} className="btn btn-ghost btn-sm" title="Edit" data-testid={`edit-btn-${d.department_code}`}><Edit size={14} /></button>
                        <button onClick={() => deleteDept(d.id, d.name)} className="btn btn-ghost btn-sm" title="Delete" data-testid={`delete-btn-${d.department_code}`}><Trash2 size={14} color="#DC2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="dept-modal">
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editingDept ? 'Edit Department' : 'Add Department'}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="name-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea className="form-textarea" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} data-testid="description-input" />
                </div>
                <div className="form-group">
                  <label className="form-label">Department Head</label>
                  <input type="text" className="form-input" value={formData.head} onChange={(e) => setFormData({ ...formData, head: e.target.value })} data-testid="head-input" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-dept-btn">{editingDept ? 'Update' : 'Add Department'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Departments;
